import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Sets document.title, meta description, og:title, og:description, og:image,
 * twitter:image and canonical on every route change. Also fires GA4 page_view.
 *
 * Signature (backwards-compatible):
 *   usePageMeta(title, description)
 *   usePageMeta(title, description, { image, ogDescription, ogTitle, canonical })
 *
 * The image URL is written to og:image + twitter:image so social scrapers pick
 * it up as the share preview thumbnail. If `ogDescription` is omitted, we fall
 * back to `description` for og:description / twitter:description.
 */
export function usePageMeta(title, description, opts) {
  const location = useLocation();
  const image = opts?.image;
  const ogTitle = opts?.ogTitle || title;
  const ogDescription = opts?.ogDescription || description;
  const canonical = opts?.canonical;

  useEffect(() => {
    if (title) document.title = title;

    // Helper: get-or-create <meta> tag with a chosen attribute (name or property).
    const upsertMeta = (attrKey, attrVal, content) => {
      if (content == null) return;
      let el = document.querySelector(`meta[${attrKey}="${attrVal}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrKey, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", ogTitle);
    upsertMeta("property", "og:description", ogDescription);
    upsertMeta("name", "twitter:title", ogTitle);
    upsertMeta("name", "twitter:description", ogDescription);

    if (image) {
      upsertMeta("property", "og:image", image);
      upsertMeta("name", "twitter:image", image);
    }

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }

    if (typeof window.__trackPageView === "function") {
      window.__trackPageView(location.pathname + location.search, title || document.title);
    }
  }, [title, description, image, ogTitle, ogDescription, canonical, location.pathname, location.search]);
}

export default usePageMeta;
